/** @jsxImportSource @emotion/react */
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { css } from '@emotion/react'
import { IconButton, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import {
  overlayStyles,
  contentStyles,
  headerStyles,
  footerStyles,
  titleStyles,
  descriptionStyles,
  closeButtonStyles,
} from './Dialog.styles'

export const Dialog = ({
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => (
  <DialogPrimitive.Root
    css={css`
      pointer-events: auto !important;
    `}
    {...props}
  >
    {children}
  </DialogPrimitive.Root>
)
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close

export const DialogPortal = ({ children }: { children: React.ReactNode }) => {
  return (
    <DialogPrimitive.Portal
      css={css`
        pointer-events: auto !important;
      `}
      container={document.body}
    >
      {children}
    </DialogPrimitive.Portal>
  )
}

export const DialogOverlay = (props: DialogPrimitive.DialogOverlayProps) => {
  return <DialogPrimitive.Overlay data-slot="dialog-overlay" css={overlayStyles()} {...props} />
}

interface DialogContentProps extends DialogPrimitive.DialogContentProps {
  showCloseButton?: boolean
  onClose?: () => void
}

export const DialogContent = ({
  children,
  showCloseButton = true,
  onClose,
  ...props
}: DialogContentProps) => {
  const theme = useTheme()

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content data-slot="dialog-content" css={contentStyles({ theme })} {...props}>
        {children}

        {showCloseButton && (
          <DialogPrimitive.Close asChild>
            <IconButton onClick={onClose} aria-label="Close" css={closeButtonStyles()}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

export const DialogHeader = ({ ...props }: React.ComponentProps<'div'>) => (
  <div data-slot="dialog-header" css={headerStyles} {...props} />
)

export const DialogFooter = ({ ...props }: React.ComponentProps<'div'>) => (
  <div data-slot="dialog-footer" css={footerStyles} {...props} />
)

export const DialogTitle = ({ ...props }: DialogPrimitive.DialogTitleProps) => {
  const theme = useTheme()
  return <DialogPrimitive.Title data-slot="dialog-title" css={titleStyles({ theme })} {...props} />
}

export const DialogDescription = ({ ...props }: DialogPrimitive.DialogDescriptionProps) => {
  const theme = useTheme()
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      css={descriptionStyles({ theme })}
      {...props}
    />
  )
}
